import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Helmet } from 'react-helmet';
import jsPDF from 'jspdf';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

import { User, CreditCard } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MembershipNomineesSection } from "@/components/MembershipNomineesSection";
import { MembershipDocumentsSection } from "@/components/MembershipDocumentsSection";
import { MembershipTermsSection } from "@/components/MembershipTermsSection";

const membershipSchema = z.object({
  // Personal Information
  fullNames: z.string().min(2, "Full names are required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  idNumber: z.string().min(6, "Valid ID number is required"),
  department: z.string().min(2, "Department is required"),
  dutyStation: z.string().min(2, "Duty station is required"),
  staffNumber: z.string().min(2, "Staff number is required"),
  postalAddress: z.string().optional(),
  postCode: z.string().optional(),
  town: z.string().optional(),
  officeTelephone: z.string().optional(),
  mobilePhone: z.string().min(10, "Valid mobile phone number is required"),
  emailAddress: z.string().email("Valid email address is required"),
  
  // Witness Information
  witnessName: z.string().min(2, "Witness name is required"),
  witnessIdNumber: z.string().min(6, "Witness ID number is required"),
  witnessDutyStation: z.string().optional(),
  
  // Payment Commitment
  monthlyContribution: z.number().min(2000, "Minimum contribution is Ksh. 2,000"),
  paymentMethod: z.enum(["Payslip Checkoff", "Direct Deposit"]),
  startMonth: z.string().min(1, "Start month is required"),
  registeredMpesa: z.string().min(10, "M-Pesa number is required"),
  
  // Nominee 1 (Required)
  nominee1FullNames: z.string().min(2, "Nominee 1 full names are required"),
  nominee1DateOfBirth: z.string().optional(),
  nominee1IdNumber: z.string().min(6, "Nominee 1 ID number is required"),
  nominee1Relationship: z.string().optional(),
  nominee1PostalAddress: z.string().optional(),
  nominee1Email: z.string().email().optional().or(z.literal("")),
  nominee1Phone: z.string().optional(),
  nominee1Percentage: z.number().min(1).max(100, "Percentage must be between 1-100"),
  
  // Nominee 2 (Optional)
  nominee2FullNames: z.string().optional(),
  nominee2DateOfBirth: z.string().optional(),
  nominee2IdNumber: z.string().optional(),
  nominee2Relationship: z.string().optional(),
  nominee2PostalAddress: z.string().optional(),
  nominee2Email: z.string().email().optional().or(z.literal("")),
  nominee2Phone: z.string().optional(),
  nominee2Percentage: z.number().min(0).max(100).optional(),
  
  // Terms Agreement
  termsAgreed: z.boolean().refine(val => val === true, "You must agree to the terms and conditions")
}).refine(data => {
  if (data.nominee2FullNames) {
    const total = data.nominee1Percentage + (data.nominee2Percentage || 0);
    return total === 100;
  }
  return data.nominee1Percentage === 100;
}, {
  message: "Nominee percentages must total 100%",
  path: ["nominee1Percentage"]
});

type MembershipFormData = z.infer<typeof membershipSchema>;

export const MembershipApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{
    kraPinDocument?: File;
    idCopyDocument?: File;
    passportPhoto?: File;
  }>({});

  const form = useForm<MembershipFormData>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      monthlyContribution: 2000,
      nominee1Percentage: 100,
      nominee2Percentage: 0,
      termsAgreed: false
    }
  });

  const handleFileUpload = (field: 'kraPinDocument' | 'idCopyDocument' | 'passportPhoto', file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadedFiles(prev => ({ ...prev, [field]: file }));
    toast.success(`${file.name} uploaded successfully`);
  };

  const generatePDF = async (data: MembershipFormData) => {
    const pdf = new jsPDF();
    
    // Header with SACCO branding
    pdf.setFontSize(16);
    pdf.setTextColor(10, 111, 78); // Primary green color
    pdf.text('NATCON STAFF SACCO LTD', 20, 20);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('KCB Towers, Kenya Road', 140, 15);
    pdf.text('P.O. Box 21046-00100', 140, 20);
    pdf.text('Tel: +254111485951', 140, 25);
    pdf.text('Email: natconsacco@nca.go.ke', 140, 30);
    
    // Title
    pdf.setFontSize(14);
    pdf.setTextColor(10, 111, 78);
    pdf.text('MEMBERSHIP APPLICATION FORM', 20, 45);
    
    let yPosition = 60;
    
    // Personal Information Section
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('PERSONAL INFORMATION', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    
    const personalFields = [
      { label: 'Full Names:', value: data.fullNames },
      { label: 'Date of Birth:', value: data.dateOfBirth },
      { label: 'ID Number:', value: data.idNumber },
      { label: 'Department:', value: data.department },
      { label: 'Duty Station:', value: data.dutyStation },
      { label: 'Staff Number:', value: data.staffNumber },
      { label: 'Postal Address:', value: data.postalAddress || 'N/A' },
      { label: 'Post Code:', value: data.postCode || 'N/A' },
      { label: 'Town:', value: data.town || 'N/A' },
      { label: 'Office Telephone:', value: data.officeTelephone || 'N/A' },
      { label: 'Mobile Phone:', value: data.mobilePhone },
      { label: 'Email Address:', value: data.emailAddress },
    ];
    
    personalFields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 20, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 80, yPosition);
      yPosition += 8;
    });
    
    // Witness Information Section
    yPosition += 10;
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('WITNESS INFORMATION', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    
    const witnessFields = [
      { label: 'Witness Name:', value: data.witnessName },
      { label: 'Witness ID Number:', value: data.witnessIdNumber },
      { label: 'Witness Duty Station:', value: data.witnessDutyStation || 'N/A' },
    ];
    
    witnessFields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 20, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 80, yPosition);
      yPosition += 8;
    });
    
    // Payment Commitment Section
    yPosition += 10;
    if (yPosition > 230) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('PAYMENT COMMITMENT', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    
    const paymentFields = [
      { label: 'Monthly Contribution:', value: `Ksh. ${data.monthlyContribution.toLocaleString()}` },
      { label: 'Payment Method:', value: data.paymentMethod },
      { label: 'Start Month:', value: data.startMonth },
      { label: 'Registered M-Pesa:', value: data.registeredMpesa },
    ];
    
    paymentFields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 20, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 80, yPosition);
      yPosition += 8;
    });
    
    // Nominee Information Section
    yPosition += 10;
    if (yPosition > 200) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('NOMINEE INFORMATION', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    
    // Nominee 1
    pdf.setFont(undefined, 'bold');
    pdf.text('Nominee 1:', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    
    const nominee1Fields = [
      { label: 'Full Names:', value: data.nominee1FullNames },
      { label: 'Date of Birth:', value: data.nominee1DateOfBirth || 'N/A' },
      { label: 'ID Number:', value: data.nominee1IdNumber },
      { label: 'Relationship:', value: data.nominee1Relationship || 'N/A' },
      { label: 'Postal Address:', value: data.nominee1PostalAddress || 'N/A' },
      { label: 'Email:', value: data.nominee1Email || 'N/A' },
      { label: 'Phone:', value: data.nominee1Phone || 'N/A' },
      { label: 'Percentage:', value: `${data.nominee1Percentage}%` },
    ];
    
    nominee1Fields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 25, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 85, yPosition);
      yPosition += 8;
    });
    
    // Nominee 2 (if provided)
    if (data.nominee2FullNames) {
      yPosition += 5;
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }
      
      pdf.setFont(undefined, 'bold');
      pdf.text('Nominee 2:', 20, yPosition);
      yPosition += 8;
      pdf.setFont(undefined, 'normal');
      
      const nominee2Fields = [
        { label: 'Full Names:', value: data.nominee2FullNames },
        { label: 'Date of Birth:', value: data.nominee2DateOfBirth || 'N/A' },
        { label: 'ID Number:', value: data.nominee2IdNumber || 'N/A' },
        { label: 'Relationship:', value: data.nominee2Relationship || 'N/A' },
        { label: 'Postal Address:', value: data.nominee2PostalAddress || 'N/A' },
        { label: 'Email:', value: data.nominee2Email || 'N/A' },
        { label: 'Phone:', value: data.nominee2Phone || 'N/A' },
        { label: 'Percentage:', value: `${data.nominee2Percentage || 0}%` },
      ];
      
      nominee2Fields.forEach((field) => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.setFont(undefined, 'bold');
        pdf.text(field.label, 25, yPosition);
        pdf.setFont(undefined, 'normal');
        pdf.text(field.value, 85, yPosition);
        yPosition += 8;
      });
    }
    
    // Terms Agreement
    yPosition += 10;
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('DECLARATION', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.text('I agree to the terms and conditions of membership: YES', 20, yPosition);
    yPosition += 10;
    
    pdf.text(`Applicant Name: ${data.fullNames}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Date: ${new Date().toLocaleDateString()}`, 20, yPosition);
    
    return pdf;
  };

  const onSubmit = async (data: MembershipFormData) => {
    if (!uploadedFiles.kraPinDocument || !uploadedFiles.idCopyDocument || !uploadedFiles.passportPhoto) {
      toast.error("Please upload all required documents");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Generate PDF
      const pdf = await generatePDF(data);
      const pdfBlob = pdf.output('blob');
      const filename = `membership_application_${data.fullNames.replace(/\s+/g, '_')}.pdf`;

      // Prepare email content
      const subjectText = `${data.fullNames} - Member Application`;
      const bodyText = `Dear NATCON Staff Sacco Team,

I am writing to submit my membership application to join NATCON Staff Sacco.

Personal Details:
Name: ${data.fullNames}
ID Number: ${data.idNumber}
Department: ${data.department}
Staff Number: ${data.staffNumber}
Mobile Phone: ${data.mobilePhone}
Email: ${data.emailAddress}

Payment Commitment:
Monthly Contribution: Ksh. ${data.monthlyContribution.toLocaleString()}
Payment Method: ${data.paymentMethod}
Start Month: ${data.startMonth}

Nominee Information:
Primary Nominee: ${data.nominee1FullNames} (${data.nominee1Percentage}%)${data.nominee2FullNames ? `
Secondary Nominee: ${data.nominee2FullNames} (${data.nominee2Percentage || 0}%)` : ''}

Please find attached:
1. Completed membership application form (PDF)
2. KRA PIN document
3. ID/Passport copy
4. Passport photo

I have read and agreed to all terms and conditions of membership.

Thank you for considering my application.

Best regards,
${data.fullNames}
Staff Number: ${data.staffNumber}
Mobile: ${data.mobilePhone}`;

      // Create file objects for sharing
      const files: File[] = [
        new File([pdfBlob], filename, { type: 'application/pdf' })
      ];

      // Add uploaded documents to files array
      if (uploadedFiles.kraPinDocument) {
        files.push(new File([uploadedFiles.kraPinDocument], `KRA_PIN_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.kraPinDocument.name.split('.').pop()}`, { type: uploadedFiles.kraPinDocument.type }));
      }
      if (uploadedFiles.idCopyDocument) {
        files.push(new File([uploadedFiles.idCopyDocument], `ID_Copy_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.idCopyDocument.name.split('.').pop()}`, { type: uploadedFiles.idCopyDocument.type }));
      }
      if (uploadedFiles.passportPhoto) {
        files.push(new File([uploadedFiles.passportPhoto], `Passport_Photo_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.passportPhoto.name.split('.').pop()}`, { type: uploadedFiles.passportPhoto.type }));
      }

      // Check if Web Share API is available and can share files
      if ('share' in navigator && 'canShare' in navigator) {
        // Check if the browser can share files
        if (navigator.canShare({ files })) {
          try {
            await navigator.share({
              files,
              title: subjectText,
              text: `To: natconsacco@nca.go.ke\nSubject: ${subjectText}\n\n${bodyText}`,
            });
            
            toast.success("Email client opened with application and documents attached. Complete sending from your email app.");
            form.reset();
            setUploadedFiles({});
            return;
          } catch (error: any) {
            // User cancelled or share failed
            if (error.name !== 'AbortError') {
              console.warn('Share failed:', error);
            }
          }
        }
      }

      // Fallback: Download all files and open email with instructions
      // Download PDF
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfLink = document.createElement('a');
      pdfLink.href = pdfUrl;
      pdfLink.download = filename;
      pdfLink.style.display = 'none';
      document.body.appendChild(pdfLink);
      pdfLink.click();
      document.body.removeChild(pdfLink);
      URL.revokeObjectURL(pdfUrl);

      // Download KRA PIN document
      if (uploadedFiles.kraPinDocument) {
        const kraUrl = URL.createObjectURL(uploadedFiles.kraPinDocument);
        const kraLink = document.createElement('a');
        kraLink.href = kraUrl;
        kraLink.download = `KRA_PIN_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.kraPinDocument.name.split('.').pop()}`;
        kraLink.style.display = 'none';
        document.body.appendChild(kraLink);
        kraLink.click();
        document.body.removeChild(kraLink);
        URL.revokeObjectURL(kraUrl);
      }

      // Download ID Copy document
      if (uploadedFiles.idCopyDocument) {
        const idUrl = URL.createObjectURL(uploadedFiles.idCopyDocument);
        const idLink = document.createElement('a');
        idLink.href = idUrl;
        idLink.download = `ID_Copy_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.idCopyDocument.name.split('.').pop()}`;
        idLink.style.display = 'none';
        document.body.appendChild(idLink);
        idLink.click();
        document.body.removeChild(idLink);
        URL.revokeObjectURL(idUrl);
      }

      // Download Passport Photo
      if (uploadedFiles.passportPhoto) {
        const photoUrl = URL.createObjectURL(uploadedFiles.passportPhoto);
        const photoLink = document.createElement('a');
        photoLink.href = photoUrl;
        photoLink.download = `Passport_Photo_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.passportPhoto.name.split('.').pop()}`;
        photoLink.style.display = 'none';
        document.body.appendChild(photoLink);
        photoLink.click();
        document.body.removeChild(photoLink);
        URL.revokeObjectURL(photoUrl);
      }

      // Create email with pre-filled content
      const subject = encodeURIComponent(subjectText);
      const body = encodeURIComponent(bodyText);
      const mailtoLink = `mailto:natconsacco@nca.go.ke?subject=${subject}&body=${body}`;
      
      // Wait a moment for downloads to start, then open email
      setTimeout(() => {
        window.location.href = mailtoLink;
        
        toast.success("Files downloaded and email client opening. Please attach the downloaded files and send.");
      }, 2000);

      form.reset();
      setUploadedFiles({});
    } catch (error: any) {
      toast.error("Failed to generate application. Please try again.");
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
      <Helmet>
        <title>NATCON Staff Sacco Membership Application Form</title>
        <meta name="description" content="A modern, highly effective web-based application form for new members of the NATCON Staff Sacco. The page is designed for a seamless user experience, incorporating elegant animations and a clean, responsive layout." />
        <meta name="keywords" content="NATCON, Staff Sacco, membership, application, form" />
        <link rel="canonical" href="/membership-application" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent mb-4">
              NATCON Staff Sacco
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground/90 mb-6">
              Membership Application Form
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join the NATCON Staff Sacco by completing this form. All information is confidential and will be used solely for membership processing.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              
              {/* Personal Information Section */}
              <Card className="form-section-card animate-fade-in hover-scale">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Please provide your personal details as they appear on your official documents.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="fullNames"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Names (Mr/Mrs/Ms)</FormLabel>
                          <FormControl>
                            <Input placeholder="As they appear on ID" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="idNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ID Number</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="department"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Department</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="dutyStation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duty Station</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="staffNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Staff Number</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={form.control}
                      name="postalAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Address</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="postCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Post Code</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="town"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Town</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="officeTelephone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Office Telephone Number</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="mobilePhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mobile Phone Number *</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="emailAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail Address *</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} className="form-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-semibold mb-4">Passport Photo Upload</h4>
                    <div className="space-y-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-4">
                          <Input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload('passportPhoto', file);
                            }}
                            className="form-input"
                            id="passport-photo-upload"
                          />
                          {uploadedFiles.passportPhoto && (
                            <span className="text-sm text-primary font-medium">
                              ✓ Uploaded
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Upload a recent passport-size photo (JPEG/PNG, max 10MB)
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4">Witness Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="witnessName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness (Sacco Member) Name *</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="witnessIdNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness ID Number *</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="witnessDutyStation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness Duty Station</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Commitment Section */}
              <Card className="form-section-card animate-fade-in hover-scale">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    Payment Commitment
                  </CardTitle>
                  <CardDescription>
                    Please specify your monthly contribution and preferred payment method.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="monthlyContribution"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>I hereby commit to pay NATCON Sacco Ltd Kshs. *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              min="2000"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                              className="form-input"
                            />
                          </FormControl>
                          <FormDescription>Minimum contribution: Ksh. 2,000</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="startMonth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>With effect from the month of *</FormLabel>
                          <FormControl>
                            <Input type="month" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Monthly through: *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col space-y-2"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Payslip Checkoff" id="payslip" />
                              <label htmlFor="payslip">Payslip Checkoff</label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Direct Deposit" id="direct" />
                              <label htmlFor="direct">Direct Deposit</label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="registeredMpesa"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registered M-Pesa Number *</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} className="form-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="bg-accent/20 p-4 rounded-lg border border-accent">
                    <h4 className="font-semibold mb-2">For Direct Deposits:</h4>
                    <p><strong>Paybill Number:</strong> 400222</p>
                    <p><strong>Account Number:</strong> 412443#[name][purpose]</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Example: 412443#janedoeLoan
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Nominated Beneficiaries Section */}
              <MembershipNomineesSection form={form} />

              {/* Required Attachments Section */}
              <MembershipDocumentsSection 
                uploadedFiles={uploadedFiles}
                onFileUpload={handleFileUpload}
              />

              {/* Terms and Conditions Section */}
              <MembershipTermsSection 
                form={form}
                isSubmitting={isSubmitting}
              />
              
            </form>
          </Form>
        </div>
      </main>

      <Footer />
    </div>
  );
};